import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, ArrowUpRight, Shield, LogOut } from 'lucide-react';
import scrappyCar from '../assets/scrappy-car.webp'; // Audit 2026-09-20, punto #20: WebP invece di PNG (-57% peso)
import { httpsCallable } from 'firebase/functions';
import { ref as dbRef, onValue } from 'firebase/database';
import { functions, database } from '../config/firebase';
import { getAuth, signOut, signInWithCustomToken } from 'firebase/auth';
import { useCompany } from '../contexts/CompanyContext';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import { endImpersonationFn } from '../utils/platformAdminFunctions';

/**
 * ImpersonationBanner — banner sticky "stai impersonando X, esci" con il
 * relativo pulsante di uscita.
 *
 * PRIMA di questo fix il banner viveva solo dentro AdminDashboard.js, quindi
 * spariva ogni volta che RequireAdmin (in App.js) mostrava AccountBlockedScreen
 * o SubscriptionBlockedScreen al posto della dashboard (account sospeso o
 * abbonamento scaduto per l'azienda impersonata): il platform admin restava
 * bloccato su quelle schermate senza alcun modo di uscire dalla sessione di
 * impersonazione e tornare al proprio pannello, se non svuotando manualmente
 * il sessionStorage o cancellando i cookie. Estratto qui come componente
 * condiviso, montato da RequireAdmin PRIMA di decidere quale schermata
 * mostrare, così resta visibile in ogni caso durante un'impersonazione.
 */
export function ImpersonationBanner() {
  let imp = null;
  try { imp = JSON.parse(sessionStorage.getItem('impersonating')); } catch { imp = null; }
  if (!imp) return null;

  const exitImpersonation = async () => {
    try {
      // Genera un nuovo custom token per il platform admin originale mentre
      // siamo ancora autenticati come l'owner impersonato (serve il claim
      // impersonatedByUid presente nel suo token), così si rientra nel
      // pannello senza dover rifare il login.
      const res = await endImpersonationFn();
      const { customToken } = res.data;
      sessionStorage.removeItem('impersonating');
      await signInWithCustomToken(getAuth(), customToken);
    } catch (err) {
      console.error('Errore durante il rientro al pannello platform admin:', err);
      // Fallback: se il rientro automatico fallisce, esce comunque per non
      // lasciare l'admin bloccato nella sessione del cliente.
      sessionStorage.removeItem('impersonating');
      await signOut(getAuth());
    }
    window.location.href = '/platform-admin';
  };

  return (
    <div className="sticky top-0 z-50 bg-indigo-700 text-white px-4 py-2.5 flex items-center justify-between gap-3 shadow-lg">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Shield className="w-4 h-4 shrink-0" />
        Stai visualizzando la dashboard di <strong className="underline">{imp.ragioneSociale}</strong> come platform admin
      </div>
      <button
        onClick={exitImpersonation}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-indigo-700 text-xs font-bold hover:bg-indigo-50 transition shrink-0"
      >
        <LogOut className="w-3.5 h-3.5" /> Esci e torna al pannello
      </button>
    </div>
  );
}

/**
 * SubscriptionBanner — Fase 8 del piano.
 */
export function SubscriptionBanner({ onGoToSubscription }) {
  const { subscriptionStatus, gracePeriodEndsAt } = useCompany();

  if (subscriptionStatus !== 'grace_period' || !gracePeriodEndsAt) return null;

  const msLeft = gracePeriodEndsAt - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));

  return (
    <div className="bg-red-600 text-white px-4 py-3 flex flex-wrap items-center justify-center gap-3 text-sm font-medium sticky top-0 z-50">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <span>
          {daysLeft > 0
            ? `Il pagamento del tuo abbonamento non è riuscito. Hai ${daysLeft} ${daysLeft === 1 ? 'giorno' : 'giorni'} per aggiornare il metodo di pagamento prima del blocco definitivo.`
            : 'Il pagamento del tuo abbonamento non è riuscito. Aggiornalo subito per evitare il blocco.'}
        </span>
      </div>
      {onGoToSubscription && (
        <button
          onClick={onGoToSubscription}
          className="px-3 py-1 bg-white text-red-700 font-semibold rounded-lg text-xs hover:bg-red-50 transition shrink-0"
        >
          Aggiorna pagamento
        </button>
      )}
    </div>
  );
}

/**
 * StorageQuotaBanner — Fase 9 del piano.
 */
export function StorageQuotaBanner({ onGoToSubscription, onGoToCars }) {
  const { companyId } = useCompany();
  const [exceeded, setExceeded] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    const unsub = onValue(dbRef(database, `companies/${companyId}/storageQuotaExceeded`), (snap) => {
      setExceeded(Boolean(snap.val()));
    });
    return () => unsub();
  }, [companyId]);

  if (!exceeded) return null;

  return (
    <div className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2.5 text-center text-sm flex flex-wrap items-center justify-center gap-2">
      <span>
        Hai superato lo spazio foto del tuo piano. Elimina le foto non necessarie dai veicoli in catalogo, oppure aggiungi <strong>+5 GB a 5 €/mese</strong>.
      </span>
      <div className="flex items-center gap-2">
        {onGoToCars && (
          <button
            onClick={onGoToCars}
            className="px-2.5 py-0.5 bg-amber-200 hover:bg-amber-300 text-amber-900 rounded font-semibold text-xs transition"
          >
            Gestisci veicoli
          </button>
        )}
        {onGoToSubscription && (
          <button
            onClick={onGoToSubscription}
            className="px-2.5 py-0.5 bg-amber-800 hover:bg-amber-900 text-white rounded font-semibold text-xs transition"
          >
            Aggiungi spazio (+5 GB)
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── CSS identico alla pagina prezzi marketing ─────────────────────────── */
const PRICING_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=DM+Sans:wght@400;500&display=swap');

  .sbs-wrap {
    min-height: 100vh;
    background: #F8FAFC;
    padding: 3.5rem 1.5rem 4rem;
    font-family: 'DM Sans', sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .sbs-top {
    text-align: center;
    max-width: 520px;
    margin: 0 auto 3rem;
  }
  .sbs-lock {
    width: 12rem; height: auto;
    background: transparent; border-radius: 0;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 1.25rem;
  }
  .sbs-lock img { width: 100%; height: auto; display: block; }
  .sbs-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: clamp(1.6rem, 3vw, 2.1rem);
    font-weight: 800; color: #0F172A;
    letter-spacing: -.03em; margin-bottom: .6rem;
  }
  .sbs-lead {
    font-size: .97rem; color: #64748B; line-height: 1.75;
  }
  .sbs-error {
    margin-top: 1rem; font-size: .85rem;
    color: #B91C1C; background: #FEF2F2;
    border: 1px solid #FECACA; border-radius: 8px;
    padding: .65rem 1rem;
  }

  /* Grid piani */
  .sbs-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
    max-width: 1040px;
    margin: 0 auto;
    align-items: start;
  }
  @media (max-width: 860px) {
    .sbs-grid { grid-template-columns: 1fr; max-width: 420px; }
  }

  /* Card — identica a .pc della pagina prezzi */
  .sbs-pc {
    background: #fff;
    border: 1.5px solid #E2E8F0;
    border-radius: 16px;
    padding: 2rem;
    position: relative;
    transition: box-shadow .3s;
    min-width: 0;
  }
  .sbs-pc:hover { box-shadow: 0 20px 60px rgba(15,23,42,.10), 0 8px 24px rgba(15,23,42,.06); }
  .sbs-pc.pop {
    border-color: #2563EB;
    border-width: 2px;
    box-shadow: 0 12px 40px rgba(37,99,235,.14);
    transform: translateY(-6px);
  }

  /* Badge "Più scelto" — identico a .pop-badge */
  .sbs-pop-badge {
    position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
    background: #2563EB; color: #fff;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: .63rem; font-weight: 800;
    letter-spacing: .1em; text-transform: uppercase;
    padding: .28rem .9rem; border-radius: 100px; white-space: nowrap;
  }

  /* Tag colorato — identico a .ptag */
  .sbs-ptag {
    display: inline-flex; align-items: center; gap: .35rem;
    font-size: .7rem; font-weight: 700;
    padding: .25rem .7rem; border-radius: 100px;
    margin-bottom: 1rem;
  }
  .sbs-ptag.free { background: #DCFCE7; color: #16A34A; }
  .sbs-ptag.best { background: #EFF6FF; color: #2563EB; }
  .sbs-ptag.warn { background: #FEF3C7; color: #D97706; }

  /* Price header — identico a .price-header */
  .sbs-price-header {
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #E2E8F0;
  }
  .sbs-price-tier {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: .68rem; font-weight: 700;
    letter-spacing: .16em; text-transform: uppercase;
    color: #64748B; display: block; margin-bottom: .6rem;
  }
  .sbs-price-row {
    display: flex; align-items: flex-end; gap: .15rem; line-height: 1;
  }
  .sbs-price-currency {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.3rem; font-weight: 700; color: #0F172A;
    padding-bottom: .3rem;
  }
  .sbs-price-number {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 3rem; font-weight: 800; color: #0F172A;
    letter-spacing: -.04em; line-height: 1;
  }
  .sbs-price-cents {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.3rem; font-weight: 700; color: #0F172A;
    padding-bottom: .3rem;
  }
  .sbs-price-period {
    font-size: .82rem; color: #64748B;
    margin-top: .4rem; display: block;
  }

  /* Note box — identico a .pnote */
  .sbs-pnote {
    font-size: .81rem; color: #334155;
    background: #F1F5F9; border-radius: 8px;
    padding: .65rem .85rem; margin-bottom: 1.25rem;
    border-left: 3px solid #CBD5E1; line-height: 1.6;
  }
  .sbs-pnote.ok { border-left-color: #16A34A; background: #DCFCE7; color: #14532D; }
  .sbs-pnote.warn { border-left-color: #D97706; background: #FEF3C7; color: #92400E; }

  /* Lista feature — identica a .pl */
  .sbs-pl {
    list-style: none; padding: 0; margin: 0 0 1.85rem;
    display: flex; flex-direction: column; gap: .65rem;
  }
  .sbs-pl li {
    display: flex; align-items: flex-start; gap: .5rem;
    font-size: .87rem; color: #334155; line-height: 1.45;
  }
  .sbs-ck { color: #16A34A; flex-shrink: 0; margin-top: .05rem; font-size: .9rem; font-weight: 700; }
  .sbs-cx { color: #94A3B8; flex-shrink: 0; margin-top: .05rem; font-size: .9rem; }

  /* CTA button — identico a .btn */
  .sbs-btn {
    display: flex; align-items: center; justify-content: center; gap: .5rem;
    width: 100%;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: .85rem; font-weight: 700;
    letter-spacing: -.01em;
    padding: .78rem 1.6rem;
    border: none; cursor: pointer;
    border-radius: 10px;
    transition: all .22s ease;
    text-decoration: none;
  }
  .sbs-btn.primary {
    background: #2563EB; color: #fff;
  }
  .sbs-btn.primary:hover {
    background: #3B82F6;
    transform: translateY(-1px);
    box-shadow: 0 6px 22px rgba(37,99,235,.35);
  }
  .sbs-btn.outline {
    background: transparent; color: #2563EB;
    border: 1.5px solid #CBD5E1;
  }
  .sbs-btn.outline:hover {
    border-color: #2563EB; background: #EFF6FF;
  }
  .sbs-btn:disabled {
    opacity: .5; cursor: not-allowed; transform: none !important;
    box-shadow: none !important;
  }

  /* Footer note */
  .sbs-footer {
    text-align: center; font-size: .78rem;
    color: #94A3B8; margin-top: 2rem;
  }
`;

const PLANS = [
  {
    id: 'base',
    tier: 'Base',
    price: '34',
    cents: ',99',
    period: 'al mese + IVA',
    tag: null,
    tagVariant: null,
    note: <>Ideale per chi gestisce <strong>fino a 10 veicoli</strong>. <strong>34,99€/mese</strong>.</>,
    noteVariant: 'default',
    featured: false,
    ctaLabel: 'Attiva piano Base',
    ctaVariant: 'outline',
    items: [
      { ok: true,  label: <>Fino a <strong>10 veicoli</strong></> },
      { ok: true,  label: <><strong>2 GB</strong> database inclusi</> },
      { ok: true,  label: 'Sito di prenotazione online' },
      { ok: true,  label: 'Pagamenti online Stripe' },
      { ok: true,  label: 'Contratti PDF automatici' },
      { ok: true,  label: '1 Admin' },
      { ok: false, label: 'Codici sconto non inclusi' },
      { ok: false, label: 'Statistiche avanzate non incluse' },
      { ok: false, label: 'Dominio personalizzato non incluso' },
    ],
  },
  {
    id: 'pro',
    tier: 'Pro',
    price: '89',
    cents: ',99',
    period: 'al mese + IVA',
    tag: null,
    tagVariant: null,
    note: <>Il piano più completo. <strong>89,99€/mese</strong>. Statistiche, codici sconto e admin illimitati inclusi. Riattivazione immediata.</>,
    noteVariant: 'default',
    featured: true,
    ctaLabel: 'Riattiva piano Pro',
    ctaVariant: 'primary',
    items: [
      { ok: true,  label: <>Fino a <strong>40 veicoli</strong></> },
      { ok: true,  label: <><strong>5 GB</strong> database inclusi</> },
      { ok: true,  label: 'Sito di prenotazione online' },
      { ok: true,  label: 'Pagamenti online Stripe' },
      { ok: true,  label: 'Contratti PDF automatici' },
      { ok: true,  label: 'Statistiche e ricavi' },
      { ok: true,  label: 'Codici sconto' },
      { ok: true,  label: 'Admin illimitati' },
      { ok: true,  label: 'Onboarding assistito' },
      { ok: true,  label: 'Assistenza via email e chat WhatsApp' },
      { ok: true,  label: 'SLA 99.90%' },
      { ok: false, label: 'Dominio personalizzato non incluso' },
    ],
  },
  {
    id: 'business',
    tier: 'Business Pro',
    price: '169',
    cents: null,
    period: 'al mese + IVA',
    tag: 'Dominio + Hosting inclusi',
    tagVariant: 'best',
    note: <>Include <strong>dominio personalizzato</strong> e <strong>hosting dedicato</strong>. Il tuo sito su <em>tuonoleggio.it</em>.</>,
    noteVariant: 'ok',
    featured: false,
    ctaLabel: 'Attiva Business Pro',
    ctaVariant: 'outline',
    items: [
      { ok: true, label: <>Veicoli <strong>illimitati</strong></> },
      { ok: true, label: <><strong>10 GB</strong> database inclusi</> },
      { ok: true, label: 'Tutto incluso nel piano Pro' },
      { ok: true, label: <><strong>Dominio personalizzato</strong> incluso</> },
      { ok: true, label: <><strong>Hosting dedicato</strong> incluso</> },
      { ok: true, label: <><strong>Personalizzazione sito</strong> (logo, colori, brand)</> },
      { ok: true, label: 'Assistenza prioritaria' },
      { ok: true, label: <>SLA <strong>99.95%</strong> garantito</> },
    ],
  },
];

/**
 * SubscriptionBlockedScreen — sostituisce l'intero pannello admin quando
 * subscriptionStatus è 'expired'. Le card usano lo stesso identico design
 * della pagina prezzi del sito marketing (PriceCard.jsx + styles.js).
 */
export function SubscriptionBlockedScreen() {
  const [loading, setLoading] = useState(null);
  const [error, setError]     = useState('');

  const handleLogout = async () => {
    await signOut(getAuth());
    window.location.href = '/';
  };

  const checkoutFn = httpsCallable(functions, 'createBillingCheckoutSession');

  const handleReactivate = async (planId) => {
    setError('');
    setLoading(planId);
    try {
      const currentUrl = window.location.origin + window.location.pathname;
      const { data } = await checkoutFn({
        planId,
        successUrl: `${currentUrl}?billing=success`,
        cancelUrl:  `${currentUrl}?billing=cancel`,
      });
      window.location.href = data.url;
    } catch (err) {
      console.error('Errore avvio checkout riattivazione:', err);
      setError(err.message || "Errore durante l'avvio del pagamento. Riprova.");
      setLoading(null);
    }
  };

  return (
    <div className="sbs-wrap">
      <style>{PRICING_CSS}</style>

      {/* Header */}
      <div className="sbs-top">
        <div className="sbs-lock">
          <img src={scrappyCar} alt="Accesso sospeso" />
        </div>
        <h1 className="sbs-title">Accesso sospeso</h1>
        <p className="sbs-lead">
          Il pagamento non è andato a buon fine e il periodo di aggiornamento è scaduto.
          I tuoi dati sono al sicuro — scegli un piano per riottenere subito l'accesso completo.
        </p>
        {error && <div className="sbs-error">{error}</div>}
      </div>

      {/* Griglia piani — stessa struttura di PriceCard.jsx */}
      <div className="sbs-grid">
        {PLANS.map((plan) => {
          const isLoading  = loading === plan.id;
          const isDisabled = loading !== null;

          return (
            <div key={plan.id} className={`sbs-pc${plan.featured ? ' pop' : ''}`}>

              {plan.featured && <div className="sbs-pop-badge">Più scelto</div>}

              {plan.tag && (
                <div className={`sbs-ptag ${plan.tagVariant}`}>{plan.tag}</div>
              )}

              {/* Price header */}
              <div className="sbs-price-header">
                <span className="sbs-price-tier">{plan.tier}</span>
                <div className="sbs-price-row">
                  <span className="sbs-price-currency">€</span>
                  <span className="sbs-price-number">{plan.price}</span>
                  {plan.cents && <span className="sbs-price-cents">{plan.cents}</span>}
                </div>
                <span className="sbs-price-period">{plan.period}</span>
              </div>

              {/* Note */}
              {plan.note && (
                <div className={`sbs-pnote${plan.noteVariant !== 'default' ? ` ${plan.noteVariant}` : ''}`}>
                  {plan.note}
                </div>
              )}

              {/* Feature list */}
              <ul className="sbs-pl">
                {plan.items.map((item, i) => (
                  <li key={i}>
                    <span className={item.ok ? 'sbs-ck' : 'sbs-cx'}>{item.ok ? '✓' : '—'}</span>
                    <span style={item.ok ? {} : { color: '#94A3B8' }}>{item.label}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleReactivate(plan.id)}
                disabled={isDisabled}
                className={`sbs-btn ${plan.ctaVariant}`}
              >
                {isLoading ? 'Reindirizzamento…' : plan.ctaLabel}
              </button>
            </div>
          );
        })}
      </div>

      <div className="sbs-footer">
        <p>Pagamento sicuro tramite Stripe · Annulla quando vuoi</p>
        <button
          onClick={handleLogout}
          style={{
            marginTop: '1rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '.8rem',
            color: '#64748B',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          <LogOut style={{ width: '0.9rem', height: '0.9rem' }} />
          Esci dall'account
        </button>
      </div>
    </div>
  );
}

/**
 * AccountBlockedScreen — account sospeso manualmente dal platform admin.
 */
export function AccountBlockedScreen() {
  const handleLogout = async () => {
    await signOut(getAuth());
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F5F6FA' }}>
      <div className="max-w-lg w-full text-center bg-white rounded-2xl shadow-lg p-8">
        <div className="flex items-center justify-center mx-auto mb-5" style={{ width: '12rem' }}>
          <img src={scrappyCar} alt="Account sospeso" style={{ width: '100%', height: 'auto' }} />
        </div>
        <h1 className="text-2xl font-black text-slate-900 mb-2">Account sospeso</h1>
        <p className="text-slate-500">
          Il tuo account è stato sospeso dall&apos;amministrazione della piattaforma.
          I tuoi dati sono al sicuro. Per maggiori informazioni o per richiedere
          la riattivazione, contatta l&apos;assistenza.
        </p>
        <div className="flex flex-col items-center gap-3 mt-6">
          <a
            href="mailto:supporto@noleggioincloud.it"
            className="text-sm text-blue-600 hover:underline"
          >
            Contatta l&apos;assistenza
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <LogOut className="w-4 h-4" /> Esci dall&apos;account
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * TrialFleetSizeBanner (audit 2026-09-20, punto #18).
 *
 * PROBLEMA COMMERCIALE: la prova gratuita usa i limiti di Pro (40 veicoli),
 * ma dopo la prova l'unico piano a cui si può fare downgrade da un catalogo
 * pieno è Pro (89,99€) — Base (34,99€, 10 veicoli) resta bloccato lato server
 * finché il catalogo non scende sotto 10. Un noleggio che carica 25 auto
 * durante la prova scopre il salto di prezzo (2,6×) solo al termine, come
 * sorpresa. L'audit segnala che la sorpresa è il vero killer, non il prezzo:
 * questo banner la anticipa, dichiarando il salto appena il catalogo supera
 * il limite di Base, invece di aspettare la scadenza della prova.
 *
 * Il numero non è scritto a mano: viene letto da plans/base/maxVehicles
 * (usePlanFeatures), la stessa fonte usata dalle Rules — se il prezzo o il
 * limite di Base cambiano, il banner si aggiorna da solo.
 */
export function TrialFleetSizeBanner({ vehicleCount, onUpgradeClick }) {
  const { subscriptionPlan } = useCompany();
  const { features: baseFeatures } = usePlanFeatures('base');
  const { features: proFeatures } = usePlanFeatures('pro');

  if (subscriptionPlan !== 'trial') return null;
  if (!baseFeatures?.maxVehicles) return null;
  if (!(vehicleCount >= baseFeatures.maxVehicles)) return null;

  const proPrice = proFeatures?.prezzoMensileCentesimi
    ? `€${(proFeatures.prezzoMensileCentesimi / 100).toLocaleString('it-IT', { minimumFractionDigits: proFeatures.prezzoMensileCentesimi % 100 === 0 ? 0 : 2 })}`
    : 'Pro';

  return (
    <div className="bg-blue-50 border-b border-blue-200 text-blue-900 px-4 py-2.5 text-center text-sm flex flex-wrap items-center justify-center gap-2">
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span>
        Stai usando la prova con i limiti di Pro: hai già {vehicleCount} veicoli in catalogo, più dei {baseFeatures.maxVehicles} inclusi nel piano Base.
        Al termine della prova, per continuare con un catalogo di questa dimensione servirà il piano Pro ({proPrice}/mese).
      </span>
      {onUpgradeClick && (
        <button
          onClick={onUpgradeClick}
          className="flex items-center gap-1 font-bold underline underline-offset-2 text-blue-900 hover:text-blue-700 flex-shrink-0"
        >
          Vedi i piani <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * TrialBanner — mostra un banner informativo durante gli ultimi 5 giorni
 * della prova gratuita.
 */
export function TrialBanner({ onUpgradeClick }) {
  const { subscriptionStatus, trialEndsAt } = useCompany();

  if (subscriptionStatus !== 'trial' || !trialEndsAt) return null;

  const msLeft   = trialEndsAt - Date.now();
  const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));

  if (daysLeft > 5) return null;

  const isExpired = daysLeft <= 0;

  return (
    <div className={`px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium sticky top-0 z-50 ${
      isExpired ? 'bg-red-600 text-white' : 'bg-amber-400 text-amber-900'
    }`}>
      <Clock className="w-4 h-4 flex-shrink-0" />
      <span>
        {isExpired
          ? 'La prova gratuita è terminata. Scegli un piano per continuare.'
          : `Prova gratuita: ${daysLeft} ${daysLeft === 1 ? 'giorno' : 'giorni'} rimast${daysLeft === 1 ? 'o' : 'i'}. Dopo scadrà l'accesso al gestionale.`
        }
      </span>
      {onUpgradeClick && (
        <button
          onClick={onUpgradeClick}
          className={`flex items-center gap-1 font-bold underline underline-offset-2 flex-shrink-0 ${
            isExpired ? 'text-white hover:text-red-200' : 'text-amber-900 hover:text-amber-700'
          }`}
        >
          Abbonati ora <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
